/**
 * /table · Typeahead — live in-bar suggestions as the member types.
 *
 * Surfaces (in order):
 *  0. Google Places Autocomplete (server-side) · venues, cities,
 *     POIs · real-world completions
 *  1. Cuisine matches  (from CUISINES lexicon)
 *  2. City matches     (from CITIES lexicon)
 *  3. Combined phrase  ("Cuisine in City" — when the partial matches
 *                       both a cuisine and a city across the bar)
 *
 * Lexicons stay as a fast-path fallback so the Typeahead is never
 * empty even on a flaky network · Google suggestions come in async.
 *
 * Keyboard:
 *  ↓ / ↑   — move highlight
 *  Enter   — accept highlight (if any), else regular submit
 *  Esc     — dismiss without filling
 */
import React, { useMemo, useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const CITIES = [
    "Bangkok", "Bangalore", "Bengaluru", "Mumbai", "Delhi", "New Delhi",
    "Kolkata", "Chennai", "Hyderabad", "Pune", "Goa", "Ooty", "Coonoor",
    "Jaipur", "Udaipur",
    "Paris", "London", "Tokyo", "Kyoto", "Osaka", "Lima", "Lisbon",
    "Barcelona", "Madrid", "Berlin", "Vienna", "Rome", "Milan",
    "Dubai", "Abu Dhabi", "Doha", "Istanbul", "Singapore", "Hong Kong",
    "Shanghai", "Seoul", "Sydney", "Melbourne", "New York", "Manhattan",
    "San Francisco", "Los Angeles", "Chicago", "Miami", "Boston",
];

const CUISINES = [
    "Italian", "Chinese", "Japanese", "Korean", "Thai", "Vietnamese",
    "Indian", "French", "Spanish", "Mexican", "American", "British",
    "Greek", "Turkish", "Lebanese", "Moroccan", "Mediterranean",
    "Middle Eastern", "Peruvian", "Pan-Asian",
    "Sushi", "Ramen", "Omakase", "Kaiseki",
    "Dim Sum", "Pizza", "Pasta", "Tapas", "BBQ",
    "Steakhouse", "Seafood", "Vegetarian", "Vegan",
];

function buildSuggestions(rawQuery) {
    const q = (rawQuery || "").trim().toLowerCase();
    if (q.length < 1) return [];

    // Token-aware match · the last whitespace-separated chunk is the
    // "current token". E.g. "italian in lon" → match cities starting
    // with "lon", but keep "italian" as carried-context for the
    // combined phrase suggestion.
    const tokens = q.split(/\s+/);
    const last = tokens[tokens.length - 1];

    const carriedCuisine = CUISINES.find((c) =>
        tokens.slice(0, -1).join(" ").includes(c.toLowerCase()),
    );
    const carriedCity = CITIES.find((c) =>
        tokens.slice(0, -1).join(" ").includes(c.toLowerCase()),
    );

    const out = [];

    // Top: city matches on the last token (most common refine pattern)
    for (const city of CITIES) {
        if (out.length >= 6) break;
        if (city.toLowerCase().startsWith(last)) {
            const phrase = carriedCuisine
                ? `${carriedCuisine} in ${city}`
                : city;
            out.push({
                kind: carriedCuisine ? "phrase" : "city",
                label: phrase,
                hint: carriedCuisine
                    ? `${carriedCuisine.toLowerCase()} · ${city}`
                    : "destination",
                fill: phrase,
            });
        }
    }

    // Cuisines second
    for (const c of CUISINES) {
        if (out.length >= 8) break;
        if (c.toLowerCase().startsWith(last)) {
            const phrase = carriedCity
                ? `${c} in ${carriedCity}`
                : c;
            out.push({
                kind: carriedCity ? "phrase" : "cuisine",
                label: phrase,
                hint: carriedCity
                    ? `${c.toLowerCase()} · ${carriedCity}`
                    : "cuisine",
                fill: phrase,
            });
        }
    }

    return out;
}


export function Typeahead({
    query,
    visible,
    highlightIndex,
    onPick,
    onHover,
    sessionToken,
}) {
    const localSuggestions = useMemo(() => buildSuggestions(query), [query]);
    const [serverSuggestions, setServerSuggestions] = useState([]);
    const listRef = useRef(null);

    // Debounced Places Autocomplete call. We fire 250ms after the
    // last keystroke to keep API spend reasonable; session-token
    // billing means same-lifecycle queries are all billed once.
    useEffect(() => {
        if (!visible || !sessionToken) {
            setServerSuggestions([]);
            return;
        }
        const text = (query || "").trim();
        if (text.length < 2) {
            setServerSuggestions([]);
            return;
        }
        let cancelled = false;
        const t = setTimeout(async () => {
            try {
                const r = await fetch(
                    `${API}/api/table/autocomplete?q=${encodeURIComponent(text)}&session_token=${encodeURIComponent(sessionToken)}`,
                );
                if (!r.ok) throw new Error("autocomplete_failed");
                const j = await r.json();
                if (!cancelled) setServerSuggestions(j.suggestions || []);
            } catch {
                if (!cancelled) setServerSuggestions([]);
            }
        }, 250);
        return () => {
            cancelled = true;
            clearTimeout(t);
        };
    }, [query, visible, sessionToken]);

    // Merge order: server suggestions FIRST (top 4 · most relevant)
    // then local lexicon suggestions below as a fast-path fallback.
    // Dedupe by label (case-insensitive).
    const suggestions = useMemo(() => {
        const seen = new Set();
        const out = [];
        for (const s of (serverSuggestions || []).slice(0, 4)) {
            const k = (s.label || "").toLowerCase();
            if (!k || seen.has(k)) continue;
            seen.add(k);
            out.push(s);
        }
        for (const s of localSuggestions) {
            const k = (s.label || "").toLowerCase();
            if (!k || seen.has(k)) continue;
            seen.add(k);
            out.push(s);
        }
        return out.slice(0, 8);
    }, [serverSuggestions, localSuggestions]);

    // Scroll the highlighted item into view when arrow keys move it.
    useEffect(() => {
        if (!listRef.current) return;
        const el = listRef.current.querySelector('[data-active="true"]');
        if (el?.scrollIntoView) el.scrollIntoView({ block: "nearest" });
    }, [highlightIndex]);

    if (!visible || suggestions.length === 0) return null;

    return (
        <div
            ref={listRef}
            data-testid="table-typeahead"
            style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                marginTop: 8,
                background: "var(--t-card)",
                border: "1px solid var(--t-line2)",
                borderRadius: 14,
                boxShadow: "0 18px 48px rgba(43,35,26,0.14), 0 4px 12px rgba(43,35,26,0.06)",
                overflow: "hidden",
                zIndex: 60,
            }}
        >
            {suggestions.map((s, i) => {
                const active = i === highlightIndex;
                return (
                    <button
                        key={`${s.kind}-${s.label}`}
                        type="button"
                        data-testid={`typeahead-item-${i}`}
                        data-active={active ? "true" : "false"}
                        onMouseDown={(e) => {
                            // Prevent the bar's blur from closing the
                            // overlay before the click registers.
                            e.preventDefault();
                            onPick?.(s);
                        }}
                        onMouseEnter={() => onHover?.(i)}
                        style={{
                            display: "flex",
                            alignItems: "baseline",
                            justifyContent: "space-between",
                            gap: 12,
                            width: "100%",
                            padding: "10px 14px",
                            border: 0,
                            background: active ? "var(--t-paper2)" : "transparent",
                            cursor: "pointer",
                            textAlign: "left",
                            transition: "background 80ms ease",
                            fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                        }}
                    >
                        <span style={{ fontSize: 14, color: "var(--t-ink)" }}>
                            {s.label}
                        </span>
                        <span
                            className="t-mono uppercase"
                            style={{
                                fontSize: 9,
                                letterSpacing: "0.18em",
                                color: "var(--t-ink3)",
                            }}
                        >
                            {s.hint}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

export { buildSuggestions };
